import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus } from "lucide-react";
import { toast } from "sonner";
import MobileShell from "@/components/MobileShell";
import AdminDisplayPreference, { adminShellClassName, useAdminDisplayPreference } from "@/components/AdminDisplayPreference";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import AdminHomeButton from "@/components/AdminHomeButton";

const EMPTY_FORM = { display_name: "", username: "", password: "" };

export default function AdminTherapists() {
  const navigate = useNavigate();
  const [therapists, setTherapists] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [displayPreference, setDisplayPreference] = useAdminDisplayPreference();

  const loadTherapists = async () => {
    const { data } = await api.get("/admin/therapists");
    setTherapists(data);
  };

  useEffect(() => {
    loadTherapists().catch(() => toast.error("Unable to load therapist accounts."));
  }, []);

  const createTherapist = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/therapists", form);
      setForm(EMPTY_FORM);
      await loadTherapists();
      toast.success("Therapist account created");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Unable to create therapist account.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileShell contentClassName={adminShellClassName(displayPreference)}>
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate("/admin")} className="text-slate-500 mb-3" aria-label="Back to patients">
              <ArrowLeft size={20} />
            </button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Clinic admin</p>
            <h1 className="text-2xl font-bold font-display text-slate-900 mt-1">Therapists</h1>
            <p className="text-sm text-slate-500 mt-1">Create and manage individual staff sign-ins.</p>
          </div>
          <div className="flex flex-wrap gap-2"><AdminHomeButton /><AdminDisplayPreference value={displayPreference} onChange={setDisplayPreference} /></div>
        </div>
      </header>

      <main className="px-6 pb-8 grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <form onSubmit={createTherapist} className="rounded-2xl border border-slate-100 bg-white p-4 space-y-4">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-teal-700" />
            <h2 className="font-bold text-slate-900">New therapist</h2>
          </div>
          <div>
            <Label htmlFor="therapist-display-name">Display name</Label>
            <Input
              id="therapist-display-name"
              value={form.display_name}
              onChange={(event) => setForm({ ...form, display_name: event.target.value })}
              required
              className="mt-1 rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="therapist-username">Username</Label>
            <Input
              id="therapist-username"
              value={form.username}
              onChange={(event) => setForm({ ...form, username: event.target.value })}
              required
              minLength={3}
              autoCapitalize="none"
              autoComplete="off"
              className="mt-1 rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="therapist-password">Temporary password</Label>
            <Input
              id="therapist-password"
              type="password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              required
              minLength={8}
              autoComplete="new-password"
              className="mt-1 rounded-xl"
            />
            <p className="mt-1 text-xs text-slate-500">Minimum 8 characters. It is not shown again after creation.</p>
          </div>
          <Button type="submit" disabled={saving} className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-800">
            {saving ? "Creating..." : "Create therapist"}
          </Button>
        </form>

        <section className="space-y-3">
          {therapists.map((therapist) => (
            <TherapistRow key={therapist.id} therapist={therapist} onUpdated={loadTherapists} />
          ))}
          {therapists.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
              No therapist accounts yet.
            </div>
          )}
        </section>
      </main>
    </MobileShell>
  );
}

function TherapistRow({ therapist, onUpdated }) {
  const [details, setDetails] = useState({
    display_name: therapist.display_name || therapist.name || "",
    username: therapist.username || "",
  });
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDetails({
      display_name: therapist.display_name || therapist.name || "",
      username: therapist.username || "",
    });
  }, [therapist]);

  const saveDetails = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/therapists/${therapist.id}`, details);
      await onUpdated();
      toast.success("Therapist details updated");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Unable to update therapist.");
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (active) => {
    setSaving(true);
    try {
      if (active) {
        await api.put(`/admin/therapists/${therapist.id}`, { active: true });
      } else {
        await api.post(`/admin/therapists/${therapist.id}/deactivate`);
      }
      await onUpdated();
      toast.success(active ? "Therapist reactivated" : "Therapist deactivated");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Unable to change account status.");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/therapists/${therapist.id}/reset-password`, { password: newPassword });
      setNewPassword("");
      toast.success("Password reset");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Unable to reset password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-2xl border border-slate-100 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-bold text-slate-900">{therapist.display_name || therapist.name}</h2>
          <p className="text-xs text-slate-500">@{therapist.username}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${therapist.active ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-600"}`}>
          {therapist.active ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor={`display-name-${therapist.id}`}>Display name</Label>
          <Input
            id={`display-name-${therapist.id}`}
            value={details.display_name}
            onChange={(event) => setDetails({ ...details, display_name: event.target.value })}
            className="mt-1 rounded-xl"
          />
        </div>
        <div>
          <Label htmlFor={`username-${therapist.id}`}>Username</Label>
          <Input
            id={`username-${therapist.id}`}
            value={details.username}
            onChange={(event) => setDetails({ ...details, username: event.target.value })}
            autoCapitalize="none"
            className="mt-1 rounded-xl"
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={saveDetails} disabled={saving}>Save details</Button>
        <Button size="sm" variant="outline" onClick={() => setActive(!therapist.active)} disabled={saving}>
          {therapist.active ? "Deactivate" : "Reactivate"}
        </Button>
      </div>

      <div className="mt-4 border-t border-slate-100 pt-4">
        <Label htmlFor={`password-${therapist.id}`}>Reset password</Label>
        <div className="mt-1 flex flex-col gap-2 sm:flex-row">
          <Input
            id={`password-${therapist.id}`}
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            minLength={8}
            autoComplete="new-password"
            placeholder="New temporary password"
            className="rounded-xl"
          />
          <Button type="button" size="sm" variant="secondary" onClick={resetPassword} disabled={saving || !newPassword}>
            Reset password
          </Button>
        </div>
      </div>
    </article>
  );
}
